import { useEffect, useMemo, useState } from "react";
import { Heart, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

/**
 * Rotates through public sponsors on the Home page.
 * Hides itself entirely if there are no public sponsors to feature.
 */
export default function FeaturedSponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    api.get("/sponsors/featured")
      .then((r) => setSponsors(r.data || []))
      .catch((err) => logError("[FeaturedSponsors] load failed:", err));
  }, []);

  useEffect(() => {
    if (sponsors.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % sponsors.length), 6000);
    return () => clearInterval(t);
  }, [sponsors.length]);

  const active = useMemo(() => sponsors[index], [sponsors, index]);
  if (!active) return null;

  return (
    <section className="py-20" style={{ backgroundColor: "#0E5C6E" }} data-testid="featured-sponsors-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-xs uppercase tracking-[0.35em] mb-4 text-white/70">
          Mahalo • Featured Sponsor
        </div>
        <Quote size={40} className="mx-auto mb-6 text-white/40" aria-hidden="true" />
        <h2 className="font-serif text-3xl md:text-5xl leading-snug text-white mb-6" data-testid="featured-sponsor-quote">
          {active.message
            ? `"${active.message}"`
            : `Because of ${active.name.split(" ")[0]}, another paw gets a second chance.`}
        </h2>
        <div className="flex items-center justify-center gap-2 flex-wrap mb-2">
          <div className="text-lg text-white font-serif" data-testid="featured-sponsor-name">
            — {active.name}
          </div>
          {active.tier_amount ? (
            <div className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: "#E27E4E", color: "white" }}>
              ${active.tier_amount}/mo sponsor
            </div>
          ) : null}
          {active.dog_name ? (
            <div className="text-xs px-3 py-1 rounded-full bg-white/10 text-white/90">
              Kōkua for {active.dog_name}
            </div>
          ) : null}
        </div>

        {sponsors.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2" aria-label="Sponsor selector">
            {sponsors.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIndex(i)}
                aria-label={`Show sponsor ${i + 1}`}
                className="w-2 h-2 rounded-full transition-all"
                style={{
                  backgroundColor: i === index ? "#E27E4E" : "rgba(255,255,255,0.3)",
                  width: i === index ? 24 : 8,
                }}
                data-testid={`featured-sponsor-dot-${i}`}
              />
            ))}
          </div>
        )}

        <div className="mt-10 text-sm text-white/70">
          <Heart size={14} className="inline mb-0.5 mr-1" fill="#E27E4E" strokeWidth={0} />
          Join our ʻohana of monthly sponsors —{" "}
          <a href="/sponsor" className="underline text-white" data-testid="featured-sponsor-cta">
            start from $10/month
          </a>
        </div>
      </div>
    </section>
  );
}
